/* Klaviyo browser bridge — Phase D.3 of the conversion-tracking plan.
 *
 * Listens to the dataLayer events published by conversion-tracker.js
 * and translates them to Klaviyo's `_learnq.push(['track', metric, props])`
 * format. Maps GA4-style events to Klaviyo's ecommerce metric names:
 *
 *   view_item           → 'Viewed Product'
 *   add_to_cart         → 'Added to Cart'
 *   begin_checkout      → 'Started Checkout'    (also fired server-side
 *                          from checkouts/create webhook for Abandoned
 *                          Checkout flow — Klaviyo de-dups by
 *                          $event_id)
 *   view_cart           → (not fired — Klaviyo has no analogous metric
 *                          and re-firing view_item on cart drawer open
 *                          would inflate engagement counts)
 *   remove_from_cart    → (not fired — same reason; Klaviyo's standard
 *                          flows don't react to removals)
 *   identify            → _learnq.push(['identify', ...]) for stitching
 *
 * Does NOT fire `Placed Order` — that's server-side from the Worker
 * (yuki-backend/src/services/klaviyo-converter.ts D.7) because the
 * thank-you page is sandboxed and can't access shadow product
 * properties from line items.
 *
 * Loaded by theme.liquid AFTER conversion-tracker.js. Skipped silently
 * when window._learnq isn't initialized (Klaviyo onsite snippet missing
 * or company id not configured).
 */

(function () {
  if (typeof window === 'undefined') return;
  // Klaviyo onsite snippet creates the global `klaviyo` (and legacy
  // `_learnq`). If neither exists, the snippet didn't load — skip.
  if (!window._learnq && !window.klaviyo) {
    console.debug('[klaviyo-tracker] Klaviyo onsite snippet not present; skipping bridge');
    return;
  }
  if (!window.dataLayer || !Array.isArray(window.dataLayer)) {
    console.warn('[klaviyo-tracker] dataLayer not initialized; conversion-tracker.js must load first');
    return;
  }

  /** Klaviyo's legacy `_learnq` API + modern `klaviyo.push` accept the
   *  same `['track', metric, props]` / `['identify', props]` shape.
   *  Use whichever is present. */
  function klPush(payload) {
    if (window._learnq && typeof window._learnq.push === 'function') {
      window._learnq.push(payload);
      return;
    }
    if (window.klaviyo && typeof window.klaviyo.push === 'function') {
      window.klaviyo.push(payload);
      return;
    }
  }

  // ── dataLayer event → Klaviyo metric mappers ───────────────────────

  function handleViewItem(event) {
    const item = (event.ecommerce && event.ecommerce.items && event.ecommerce.items[0]);
    if (!item || !item.item_id) return;
    klPush(['track', 'Viewed Product', {
      ProductID: item.item_id,
      SKU: item.item_id,
      ProductName: item.item_name,
      Categories: [item.item_category, item.item_category2, item.item_category3].filter(Boolean),
      ImageURL: item.image_url,
      URL: window.location.href,
      Brand: item.item_brand,
      Price: item.price,
      DesignUUID: item.design_uuid,
      IsSet: !!item.is_set_item,
      SetUUID: item.set_uuid,
    }]);
    // Klaviyo also expects a `Viewed Product` event to update the
    // "Recently Viewed Items" personalization block. The same metric
    // handles both.
  }

  function handleAddToCart(event) {
    const items = (event.ecommerce && event.ecommerce.items) || [];
    items.forEach(function (item) {
      if (!item || !item.item_id) return;
      // For Wall Set adds, we emit one event per "primary" item (Set
      // parent OR a single design). Constituent prints (items where
      // `set_position` is non-null AND `is_set_item` is true) are
      // skipped — they're internal accounting; Klaviyo Flow segmentation
      // on Wall Sets uses the Set parent's UUID.
      if (item.is_set_item && item.set_position) return;
      klPush(['track', 'Added to Cart', {
        ProductID: item.item_id,
        SKU: item.item_id,
        ProductName: item.item_name,
        Categories: [item.item_category, item.item_category2, item.item_category3].filter(Boolean),
        ImageURL: item.image_url,
        URL: window.location.href,
        Brand: item.item_brand,
        Price: item.price,
        Quantity: item.quantity,
        DesignUUID: item.design_uuid,
        IsSet: !!item.is_set_item,
        SetUUID: item.set_uuid,
        $value: item.price * (item.quantity || 1),
      }]);
    });
  }

  function handleBeginCheckout(event) {
    // Klaviyo's Abandoned Checkout flow listens on `Started Checkout`,
    // typically fired by the server-side checkouts/create webhook. The
    // browser-side fire here is a faster signal (fires the moment the
    // customer clicks the cart's Checkout button, BEFORE the checkout
    // page loads). We de-dup via $event_id when the server-side fires
    // too.
    const items = (event.ecommerce && event.ecommerce.items) || [];
    klPush(['track', 'Started Checkout', {
      $event_id: 'browser_' + Date.now(),
      $value: event.ecommerce && event.ecommerce.value,
      ItemNames: items.map(function (i) { return i.item_name; }).filter(Boolean),
      Items: items.filter(function (i) { return !(i.is_set_item && i.set_position); }).map(function (i) {
        return {
          ProductID: i.item_id,
          SKU: i.item_id,
          ProductName: i.item_name,
          Quantity: i.quantity,
          ItemPrice: i.price,
          RowTotal: i.price * (i.quantity || 1),
          ImageURL: i.image_url,
          ProductURL: i.design_handle ? '/products/' + i.design_handle : window.location.href,
          Categories: [i.item_category, i.item_category2, i.item_category3].filter(Boolean),
          Brand: i.item_brand,
          DesignUUID: i.design_uuid,
          IsSet: !!i.is_set_item,
          SetUUID: i.set_uuid,
        };
      }),
      CheckoutURL: window.location.href,
    }]);
  }

  function handleIdentify(event) {
    const u = event.user || {};
    if (!u.email && !u.user_id) return;
    const profile = {};
    if (u.email) profile.$email = u.email;
    if (u.user_id) profile.$id = u.user_id;
    if (u.first_name) profile.$first_name = u.first_name;
    if (u.last_name) profile.$last_name = u.last_name;
    klPush(['identify', profile]);
  }

  // ── dataLayer subscriber ───────────────────────────────────────────
  //
  // We MUST handle two cases:
  //   1. Events already pushed BEFORE this script loads (defer order
  //      can vary; conversion-tracker may have fired view_item already).
  //   2. New events pushed AFTER this script loads.
  //
  // The standard pattern: replace dataLayer.push with a wrapper that
  // delegates to the original AND notifies our handler. Then drain
  // the existing array through the same handler.

  const originalPush = window.dataLayer.push.bind(window.dataLayer);

  function handle(evt) {
    if (!evt || typeof evt !== 'object' || !evt.event) return;
    try {
      switch (evt.event) {
        case 'view_item':       handleViewItem(evt);       break;
        case 'add_to_cart':     handleAddToCart(evt);      break;
        case 'begin_checkout':  handleBeginCheckout(evt);  break;
        case 'identify':        handleIdentify(evt);       break;
        // view_cart, remove_from_cart intentionally not mapped (see header)
      }
    } catch (err) {
      console.warn('[klaviyo-tracker] handler threw for', evt.event, err);
    }
  }

  // Replace dataLayer.push (idempotent — only wrap once).
  if (!window.dataLayer.__klaviyoWrapped) {
    window.dataLayer.push = function () {
      const result = originalPush.apply(window.dataLayer, arguments);
      for (let i = 0; i < arguments.length; i++) handle(arguments[i]);
      return result;
    };
    window.dataLayer.__klaviyoWrapped = true;
  }

  // Drain any events already on the dataLayer (fired before this
  // script loaded).
  for (let i = 0; i < window.dataLayer.length; i++) handle(window.dataLayer[i]);
})();
